import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Mail, Globe, Phone, Star, Copy, Check, ExternalLink, FileText, Send,
  User, Linkedin, Twitter, Facebook, Instagram
} from 'lucide-react';

/**
 * Every stored way to reach one affiliate, in one place.
 *
 * The row-level "Contact" button used to hand a comma-joined string of email
 * addresses straight to EmailPopup, which meant a site reachable only through a
 * contact form or a Telegram channel read as "No Contact". The offline contacts
 * script stores four channels per site, so this sits in between: it lists what
 * we have, marks the one worth trying first, and hands an email on to EmailPopup
 * unchanged when that is what the user picks.
 *
 * Deliberately read-only. Nothing here triggers a lookup -- every row shown was
 * already found, which is why the popup opens instantly. The table also opens
 * it with what a "Find Contact" lookup just returned when that includes the
 * site's authors, which the composer alone has nowhere to show.
 */

/** Section order and presentation. Email first because it is the best pitch route. */
const SECTIONS = [
  { channel: 'email', label: 'Email', icon: Mail, tint: 'text-blue-600 dark:text-blue-400' },
  { channel: 'contact_form', label: 'Contact Form', icon: FileText, tint: 'text-purple-600 dark:text-purple-400' },
  { channel: 'social', label: 'Social', icon: Globe, tint: 'text-emerald-600 dark:text-emerald-400' },
  { channel: 'phone', label: 'Phone', icon: Phone, tint: 'text-amber-600 dark:text-amber-400' }
];

/** Role slugs as stored, turned into something a human reads. */
const ROLE_LABEL = {
  affiliate: 'Affiliates',
  partnerships: 'Partnerships',
  advertising: 'Advertising',
  editorial: 'Editorial',
  general: 'General',
  support: 'Support',
  personal: 'Personal',
  // A common address the mail server accepts for anyone: never proven to exist.
  guess: 'Best guess'
};

const AUTHOR_LINK_LABEL = {
  twitter: 'X / Twitter', linkedin: 'LinkedIn', facebook: 'Facebook', instagram: 'Instagram',
  youtube: 'YouTube', tiktok: 'TikTok', threads: 'Threads', muckrack: 'Muck Rack'
};
const AUTHOR_LINK_ICON = { twitter: Twitter, linkedin: Linkedin, facebook: Facebook, instagram: Instagram };

/**
 * A social row carries its platform; everything else is better described by its
 * role. Falls back to nothing rather than printing an empty pill.
 */
const badgeFor = (contact) => {
  if (contact.channel === 'social' && contact.platform) {
    return contact.platform.charAt(0).toUpperCase() + contact.platform.slice(1);
  }
  return ROLE_LABEL[contact.role] || null;
};

/** What the row shows as its primary line. */
const displayValue = (contact) => {
  if (contact.channel !== 'social') return contact.value;
  // A bare profile URL is long and mostly boilerplate; the handle is the useful part.
  return contact.value.replace(/^https?:\/\/(www\.)?/, '');
};

/**
 * Rows shown per section before the rest collapse behind a "show all".
 *
 * Most sites are small -- 87% hold ten contacts or fewer -- but a long tail is
 * not: one site in the first thousand scanned carries 247 rows, and several
 * carry 30+ social links harvested from a footer full of other companies'
 * accounts. Without a cap those sites turn the popup into a wall of noise with
 * the useful address buried in it.
 */
const SECTION_LIMIT = 8;

const ContactPopup = ({
  isOpen = false,
  onClose = () => {},
  domain = '',
  contacts = [],
  scan = null,
  // People who write for the site, from a live lookup: { name, role, url,
  // emails, social, socialByName }. Profiles in socialByName were found by
  // searching the name, not linked from the site.
  authors = [],
  onSelectEmail = () => {}
}) => {
  const [copied, setCopied] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  // Close on Escape, matching how the other popups in the app behave.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Reset per-site view state, so opening a second affiliate does not inherit
  // the sections the previous one had expanded.
  useEffect(() => {
    if (!isOpen) { setCopied(null); setExpandedSections({}); }
  }, [isOpen]);

  const grouped = useMemo(() => {
    const out = {};
    for (const c of contacts || []) {
      if (!out[c.channel]) out[c.channel] = [];
      out[c.channel].push(c);
    }
    // Within a section, surface what we trust. A "low" confidence social was
    // scraped from the markup and never confirmed as the site's own account --
    // roughly a third of them are an operator's or a partner's page picked out
    // of a footer -- so it sinks below the confirmed ones rather than competing
    // with them for the top of the list.
    for (const channel of Object.keys(out)) {
      out[channel].sort((a, b) => {
        if (a.isBest !== b.isBest) return a.isBest ? -1 : 1;
        const rank = (c) => (c.ownerOk === false ? 2 : c.confidence === 'low' ? 1 : 0);
        return rank(a) - rank(b);
      });
    }
    return out;
  }, [contacts]);

  if (!isOpen) return null;

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      setTimeout(() => setCopied((v) => (v === value ? null : v)), 1500);
    } catch {
      // Clipboard is blocked in some embedded contexts. The value is selectable
      // on screen either way, so a failure here is not worth interrupting for.
    }
  };

  const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  const total = (contacts || []).length;
  const authorList = authors || [];

  const headline = [
    total > 0 && `${total} contact${total === 1 ? '' : 's'}`,
    authorList.length > 0 && `${authorList.length} author${authorList.length === 1 ? '' : 's'}`
  ].filter(Boolean).join(' and ');

  const authorLink = (platform, href, key, muted) => {
    const Icon = AUTHOR_LINK_ICON[platform] || Globe;
    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border transition-colors ${
          muted
            ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-300'
            : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300'
        }`}
      >
        <Icon className="w-3 h-3" />
        {AUTHOR_LINK_LABEL[platform] || platform}
      </a>
    );
  };

  const renderAuthors = () => {
    if (!authorList.length) return null;
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <User className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
            Authors
          </span>
          <span className="text-[11px] text-gray-400 dark:text-slate-500">
            {authorList.length}
          </span>
        </div>

        <div className="space-y-1.5">
          {authorList.map((author) => {
            const siteLinks = Object.entries(author.social || {});
            const nameLinks = Object.entries(author.socialByName || {});
            const emails = author.emails || [];
            return (
              <div
                key={author.url || author.name}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800/50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate text-sm font-medium text-gray-800 dark:text-slate-100">
                    {author.name}
                  </span>
                  {author.url && (
                    <a
                      href={author.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Author page"
                      className="shrink-0 p-1 rounded-md text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
                {author.role && (
                  <div className="text-[11px] text-gray-500 dark:text-slate-400 truncate">
                    {author.role}
                  </div>
                )}

                {emails.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                    {emails.map(email => (
                      <button
                        key={email}
                        type="button"
                        onClick={() => onSelectEmail({ channel: 'email', value: email })}
                        title="Compose email"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Mail className="w-3 h-3" /> {email}
                      </button>
                    ))}
                  </div>
                )}

                {siteLinks.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {siteLinks.flatMap(([platform, urls]) =>
                      urls.map((href, i) => authorLink(platform, href, `${platform}-${i}`, false)))}
                  </div>
                )}

                {nameLinks.length > 0 && (
                  <div className="mt-1.5">
                    <p className="text-[10px] text-amber-700 dark:text-amber-300 mb-1">
                      Found by searching the name. Check it's the same person.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {nameLinks.flatMap(([platform, urls]) =>
                        urls.map((href, i) => authorLink(platform, href, `name-${platform}-${i}`, true)))}
                    </div>
                  </div>
                )}

                {emails.length === 0 && siteLinks.length === 0 && nameLinks.length === 0 && (
                  <p className="mt-1 text-[11px] text-gray-400 dark:text-slate-500">
                    No email or profile links on the author page.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 w-full max-w-lg max-h-[85vh] rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-blue-50 to-white dark:from-slate-800 dark:to-slate-900">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {domain}
            </h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              {headline ? `${headline} found` : 'No contacts stored'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {total === 0 && authorList.length === 0 && (
            <div className="text-center py-8">
              <Mail className="mx-auto text-gray-300 dark:text-slate-600 mb-3" size={36} />
              <p className="text-sm text-gray-600 dark:text-slate-400">
                {scan
                  ? 'This site was checked and no contact details were published.'
                  : 'No contact details stored for this site yet.'}
              </p>
            </div>
          )}

          {SECTIONS.map(({ channel, label, icon: Icon, tint }) => {
            const rows = grouped[channel];
            if (!rows || !rows.length) return null;

            const expanded = expandedSections[channel];
            const visible = expanded ? rows : rows.slice(0, SECTION_LIMIT);
            const hidden = rows.length - visible.length;

            return (
              <div key={channel}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-3.5 h-3.5 ${tint}`} />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                    {label}
                  </span>
                  <span className="text-[11px] text-gray-400 dark:text-slate-500">
                    {rows.length}
                  </span>
                </div>

                <div className="space-y-1.5">
                  {visible.map((contact) => {
                    const badge = badgeFor(contact);
                    const isEmail = contact.channel === 'email';
                    const isPhone = contact.channel === 'phone';

                    // Email hands off to the existing composer; everything else
                    // is a link, so open it rather than pretending to send mail.
                    const primaryAction = () => {
                      if (isEmail) return onSelectEmail(contact);
                      if (isPhone) return window.open(`tel:${contact.value}`, '_self');
                      return openExternal(contact.value);
                    };

                    return (
                      <div
                        key={`${contact.channel}:${contact.value}`}
                        className={`group flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                          contact.isBest
                            ? 'border-blue-300 bg-blue-50/60 dark:border-blue-700 dark:bg-blue-950/30'
                            : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800/50 dark:hover:bg-slate-800'
                        }`}
                      >
                        {contact.isBest && (
                          <Star
                            className="w-3.5 h-3.5 shrink-0 text-blue-500 fill-blue-500"
                            aria-label="Best contact"
                          />
                        )}

                        <button
                          type="button"
                          onClick={primaryAction}
                          className="flex-1 min-w-0 text-left"
                          title={contact.value}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="truncate text-sm text-gray-800 dark:text-slate-100">
                              {displayValue(contact)}
                            </span>
                            {badge && (
                              <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300">
                                {badge}
                              </span>
                            )}
                            {contact.ownerOk === false && (
                              <span
                                className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                                title="This address may belong to another company"
                              >
                                Unverified
                              </span>
                            )}
                          </div>
                          {(contact.name || contact.jobTitle) && (
                            <div className="text-[11px] text-gray-500 dark:text-slate-400 truncate mt-0.5">
                              {[contact.name, contact.jobTitle].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </button>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => copy(contact.value)}
                            title="Copy"
                            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
                          >
                            {copied === contact.value
                              ? <Check className="w-3.5 h-3.5 text-green-500" />
                              : <Copy className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={primaryAction}
                            title={isEmail ? 'Compose email' : isPhone ? 'Call' : 'Open'}
                            className="p-1.5 rounded-md text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors"
                          >
                            {isEmail
                              ? <Send className="w-3.5 h-3.5" />
                              : <ExternalLink className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {hidden > 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandedSections(prev => ({ ...prev, [channel]: true }))}
                    className="mt-1.5 text-[11px] font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                  >
                    Show {hidden} more
                  </button>
                )}
                {expanded && rows.length > SECTION_LIMIT && (
                  <button
                    type="button"
                    onClick={() => setExpandedSections(prev => ({ ...prev, [channel]: false }))}
                    className="mt-1.5 text-[11px] font-medium text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                  >
                    Show less
                  </button>
                )}
              </div>
            );
          })}

          {renderAuthors()}
        </div>
      </div>
    </div>
  );
};

export default ContactPopup;
